import { useEffect, useState } from "react";
import { useLanguage } from "../../context/LanguageContext";
import dashboardLanguage from "../../language/dashboardLanguage";
import { dashboardAPI } from "../../services/api";
import Spinner from "../common/Spinner";

function Dashboard() {
  const { language } = useLanguage();
  const text = dashboardLanguage[language];

  const [activeTab, setActiveTab] = useState("month");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [monthlyDataLoading, setMonthlyDataLoading] = useState(false);

  // Modal states
  const [showExpensesModal, setShowExpensesModal] = useState(false);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [expandedExpenseSection, setExpandedExpenseSection] = useState(null);
  const [expandedSalesSection, setExpandedSalesSection] = useState(null);

  const [dashboardData, setDashboardData] = useState({
    purchasedMilk: {
      cowLiters: 0,
      cowAmount: 0,
      buffaloLiters: 0,
      buffaloAmount: 0,
    },
    soldMilk: {
      cowLiters: 0,
      cowAmount: 0,
      buffaloLiters: 0,
      buffaloAmount: 0,
    },
    profit: 0,
  });
  const [summaryData, setSummaryData] = useState({
    totalExpenses: 0,
    totalProfit: 0,
    totalSale: 0,
  });
  const [purchaseMilkSummaryData, setPurchaseMilkSummaryData] = useState([]);
  const [purchaseMilkData, setPurchaseMilkData] = useState([]);
  const [otherExpensesData, setOtherExpensesData] = useState([]);
  const [soldMilkSummaryData, setSoldMilkSummaryData] = useState([]);
  const [otherSalesData, setOtherSalesData] = useState([]);
  const [otherExpenses, setOtherExpenses] = useState([]);
  const [salesEntries, setSalesEntries] = useState([]);
  const [monthlySoldMilk, setMonthlySoldMilk] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, [activeTab, fromDate, toDate]);

  useEffect(() => {
    loadMonthlySoldMilk();
  }, []);

  // Compute date range for the selected tab
  const getDateRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const quarter = Math.ceil(month / 3);

    if (activeTab === "month") {
      const start = `${year}-${String(month).padStart(2, "0")}-01`;
      const end = new Date(year, month, 0).toISOString().split("T")[0];
      return { year, month, quarter, start, end };
    }
    if (activeTab === "quarter") {
      const qStart = (quarter - 1) * 3 + 1;
      const qEnd = quarter * 3;
      const start = `${year}-${String(qStart).padStart(2, "0")}-01`;
      const end = new Date(year, qEnd, 0).toISOString().split("T")[0];
      return { year, month, quarter, start, end };
    }
    if (activeTab === "custom" && fromDate && toDate) {
      return { year, month, quarter, start: fromDate, end: toDate };
    }
    return { year, month, quarter, start: null, end: null };
  };

  const loadDashboardData = async () => {
    const { year, month, quarter, start, end } = getDateRange();
    if (activeTab === "custom" && (!fromDate || !toDate)) return;

    try {
      setLoading(true);

      // Call the new getDashboardData function
      const request = {
        period:
          activeTab === "month"
            ? "month"
            : activeTab === "quarter"
              ? "quarter"
              : "custom",
        year: activeTab !== "custom" ? year : null,
        month: activeTab === "month" ? month : null,
        quarter: activeTab === "quarter" ? quarter : null,
        startDate: activeTab === "custom" ? start : null,
        endDate: activeTab === "custom" ? end : null,
      };

      const dashboardDataResponse =
        await dashboardAPI.getDashboardData(request);
      console.log("Dashboard Data Response:", dashboardDataResponse);

      // Extract and set data from the API response
      if (dashboardDataResponse?.success && dashboardDataResponse?.data) {
        const data = dashboardDataResponse.data;

        // Set summary data
        if (data.summary) {
          setSummaryData({
            totalExpenses: data.summary.totalExpenses || 0,
            totalProfit: data.summary.totalProfit || 0,
            totalSale: data.summary.totalSale || 0,
          });
        }

        // Set purchase milk data (detailed table)
        if (data.purchaseMilk && Array.isArray(data.purchaseMilk)) {
          setPurchaseMilkData(data.purchaseMilk);
        }

        // Set purchase milk summary data
        if (
          data.purchaseMilkSummary &&
          Array.isArray(data.purchaseMilkSummary)
        ) {
          setPurchaseMilkSummaryData(data.purchaseMilkSummary);
        }

        // Set other expenses data (detailed table)
        if (data.otherExpenses && Array.isArray(data.otherExpenses)) {
          setOtherExpensesData(data.otherExpenses);
        }

        // Set sold milk summary data
        if (data.soldMilkSummary && Array.isArray(data.soldMilkSummary)) {
          setSoldMilkSummaryData(data.soldMilkSummary);
        }

        // Set other sales data (detailed table)
        if (data.otherSales && Array.isArray(data.otherSales)) {
          setOtherSalesData(data.otherSales);
        }
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlySoldMilk = async () => {
    try {
      setMonthlyDataLoading(true);
      const response = await dashboardAPI.getMonthlySoldMilk();
      if (response?.success) {
        setMonthlySoldMilk(response.data.reverse());
      }
    } catch (error) {
      console.error("Error loading monthly sold milk:", error);
    } finally {
      setMonthlyDataLoading(false);
    }
  };

  const handleCloseExpensesModal = () => {
    setShowExpensesModal(false);
    setExpandedExpenseSection(null);
  };
  const handleCloseSalesModal = () => {
    setShowSalesModal(false);
    setExpandedSalesSection(null);
  };

  // ── Shared sub-components ──────────────────────────────────────────────────

  // Milk rows (Cow + Buffalo)
  const MilkRows = ({ data }) => (
    <div className="space-y-2">
      <div className="grid grid-cols-3 text-left">
        <span className="font-semibold text-slate-700 dark:text-slate-300">
          {text.cow}
        </span>
        <span className="font-bold text-slate-900 dark:text-white">
          {data.cowLiters.toFixed(1)} L
        </span>
        <span className="text-slate-500 dark:text-slate-400">
          ₹{data.cowAmount.toLocaleString("en-IN")}
        </span>
      </div>
      <div className="grid grid-cols-3 text-left">
        <span className="font-semibold text-slate-700 dark:text-slate-300">
          {text.buffalo}
        </span>
        <span className="font-bold text-slate-900 dark:text-white">
          {data.buffaloLiters.toFixed(1)} L
        </span>
        <span className="text-slate-500 dark:text-slate-400">
          ₹{data.buffaloAmount.toLocaleString("en-IN")}
        </span>
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <header className="sticky top-0 z-10 bg-background-light p-4 pb-2 dark:bg-background-dark md:static md:p-6 md:pb-3">
        <div className="flex items-center justify-between md:justify-start md:hidden">
          <div className="flex items-center w-full relative">
            <img src="/images/logo.png" alt={text.pageTitle} className="logo" />
            <h1 className="absolute left-1/2 -translate-x-1/2 text-lg font-bold text-slate-900 dark:text-white">
              {text.pageTitle}
            </h1>
          </div>
        </div>

        {/* Period tabs */}
        <div className="flex gap-3 mb-3 overflow-x-auto">
          {["month", "quarter", "custom"].map((tab) => (
            <button
              key={tab}
              type="button"
              className={`flex h-10 flex-1 items-center justify-center rounded-lg text-sm ${
                activeTab === tab
                  ? "bg-primary text-white font-bold"
                  : "bg-primary/20 text-primary dark:bg-primary/30"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "month"
                ? text.month
                : tab === "quarter"
                  ? text.quarter
                  : text.customDate}
            </button>
          ))}
        </div>

        {activeTab === "custom" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="flex items-center gap-3">
              <span className="text-md font-bold text-primary w-24">
                {text.fromDate}
              </span>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-10 flex-1 rounded-lg border border-slate-300 px-3 bg-white text-sm focus:outline-none focus:border-primary"
              />
            </label>
            <label className="flex items-center gap-3">
              <span className="text-md font-bold text-primary w-24">
                {text.toDate}
              </span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-10 flex-1 rounded-lg border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:border-primary"
              />
            </label>
          </div>
        )}
      </header>

      <main className="flex-1 overflow-y-auto p-4 pt-0 pb-24 md:p-6 md:pt-0 md:pb-6">
        {loading ? (
          <Spinner />
        ) : (
          <>
            {/* ── Summary Cards ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* Total Expenses */}
              <div
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 cursor-pointer select-none transition-all hover:border-primary/50 hover:shadow-md"
                onClick={() => setShowExpensesModal(true)}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg">{text.totalExpenses}</h3>
                  <i className="fas fa-arrow-right text-primary text-sm" />
                </div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white">
                  ₹{summaryData.totalExpenses.toLocaleString("en-IN")}
                </div>
              </div>

              {/* Total Sale */}
              <div
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 cursor-pointer select-none transition-all hover:border-primary/50 hover:shadow-md"
                onClick={() => setShowSalesModal(true)}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg">{text.totalSale}</h3>
                  <i className="fas fa-arrow-right text-primary text-sm" />
                </div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white">
                  ₹{summaryData.totalSale.toLocaleString("en-IN")}
                </div>
              </div>

              {/* Total Profit */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col items-center justify-center">
                <h3 className="font-bold text-lg mb-3">{text.totalProfit}</h3>
                <span
                  className={`text-4xl font-bold ${
                    summaryData.totalProfit >= 0
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-600 dark:text-red-400"
                  }`}
                >
                  ₹{summaryData.totalProfit.toLocaleString("en-IN")}
                </span>
              </div>
            </div>

            {/* ── Monthly Sold Milk Chart ── */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 mt-5">
              <h3 className="text-lg font-bold text-primary mb-6">
                {text.soldMilkMonthWise}
              </h3>
              {monthlyDataLoading ? (
                <Spinner />
              ) : monthlySoldMilk.length > 0 ? (
                <div className="space-y-4">
                  {monthlySoldMilk.map((monthData, idx) => {
                    const maxValue = Math.max(
                      ...monthlySoldMilk.map((m) => m.totalLiters || 0),
                    );
                    const percentage =
                      maxValue > 0
                        ? (monthData.totalLiters / maxValue) * 100
                        : 0;
                    return (
                      <div key={idx} className="space-y-1">
                        <div className="flex justify-between items-center text-sm">
                          <span className="font-medium text-slate-700 dark:text-slate-300 w-20">
                            {text.months[monthData.monthName?.toLowerCase()] ||
                              monthData.monthName}
                          </span>
                          <span className="text-slate-600 dark:text-slate-400">
                            {monthData.totalLiters?.toFixed(1) || 0} L
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-8">
                          <div
                            className="bg-gradient-to-r from-primary to-primary/70 h-8 rounded-full flex items-center justify-end px-3 transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          >
                            <span className="text-white text-xs font-semibold">
                              {percentage > 20
                                ? `₹${monthData.totalAmount?.toLocaleString("en-IN") || 0}`
                                : ""}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  {text.noData}
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* ── Expenses Modal ── */}
      {showExpensesModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={handleCloseExpensesModal}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-primary rounded-t-2xl">
              <h2 className="text-lg font-bold text-white">
                <i className="fas fa-receipt mr-2" />
                {text.totalExpenses}
              </h2>
              <button
                onClick={handleCloseExpensesModal}
                className="text-white hover:text-slate-200 transition"
              >
                <i className="fas fa-times text-xl" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              {/* Purchase Milk Summary Table */}
              <div>
                <h4 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2 mb-3">
                  <i className="fas fa-truck text-primary" />
                  {text.purchaseMilk} - Summary
                </h4>
                {purchaseMilkSummaryData.length > 0 ? (
                  <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700">
                            Milk Type
                          </th>
                          <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700">
                            Total Liters
                          </th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">
                            Total Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchaseMilkSummaryData.map((item) => (
                          <tr
                            key={item.milkTypeId}
                            onClick={() =>
                              setExpandedExpenseSection(
                                expandedExpenseSection === "purchaseMilk"
                                  ? null
                                  : "purchaseMilk",
                              )
                            }
                            className="border-b border-slate-200 dark:border-slate-700 hover:bg-primary/10 dark:hover:bg-primary/20 cursor-pointer transition"
                          >
                            <td className="px-4 py-2.5 text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700">
                              {item.milkTypeName}
                            </td>
                            <td className="px-4 py-2.5 text-center font-semibold text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700">
                              {item.totalLiters.toFixed(2)} L
                            </td>
                            <td className="px-4 py-2.5 text-right font-semibold text-slate-900 dark:text-white">
                              ₹{item.totalAmount.toLocaleString("en-IN")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    No purchase milk summary available.
                  </p>
                )}
              </div>

              {/* Purchase Milk Detailed Table - Show when Summary is expanded */}
              {expandedExpenseSection === "purchaseMilk" && (
                <div>
                  <h4 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2 mb-3">
                    <i className="fas fa-truck text-primary" />
                    {text.purchaseMilk} - Details
                  </h4>
                  {purchaseMilkData.length > 0 ? (
                    <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-700">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700">
                              Name
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700">
                              Milk Type
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700">
                              Qty (L)
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700">
                              Rate
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300">
                              Date
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {purchaseMilkData.map((item) => (
                            <tr
                              key={item.purchaseId}
                              className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
                            >
                              <td className="px-4 py-2.5 text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700">
                                {item.name}
                              </td>
                              <td className="px-4 py-2.5 text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700">
                                {item.milkType}
                              </td>
                              <td className="px-4 py-2.5 text-center font-semibold text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700">
                                {item.purchaseQtyLiters.toFixed(2)}
                              </td>
                              <td className="px-4 py-2.5 text-right font-semibold text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700">
                                ₹{item.purchaseRate.toLocaleString("en-IN")}
                              </td>
                              <td className="px-4 py-2.5 text-center text-slate-500 dark:text-slate-400">
                                {new Date(item.entryDate).toLocaleDateString(
                                  "en-IN",
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      No purchase milk entries for this period.
                    </p>
                  )}
                </div>
              )}

              {/* Other Expenses section */}
              <div>
                <h4 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2 mb-3">
                  <i className="fas fa-receipt text-primary" />
                  {text.otherExpenses}
                </h4>
                {otherExpensesData.length > 0 ? (
                  <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700">
                            Product Type
                          </th>
                          <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300">
                            Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {otherExpensesData.map((item) => (
                          <tr
                            key={item.expenseId}
                            className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
                          >
                            <td className="px-4 py-2.5 text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700">
                              {item.productType}
                            </td>
                            <td className="px-4 py-2.5 text-right font-semibold text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700">
                              ₹{item.amount.toLocaleString("en-IN")}
                            </td>
                            <td className="px-4 py-2.5 text-center text-slate-500 dark:text-slate-400">
                              {new Date(item.entryDate).toLocaleDateString(
                                "en-IN",
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    No other expenses for this period.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Sales Modal ── */}
      {showSalesModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={handleCloseSalesModal}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-primary rounded-t-2xl">
              <h2 className="text-lg font-bold text-white">
                <i className="fas fa-shopping-cart mr-2" />
                {text.totalSale}
              </h2>
              <button
                onClick={handleCloseSalesModal}
                className="text-white hover:text-slate-200 transition"
              >
                <i className="fas fa-times text-xl" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              {/* Sold Milk Summary section */}
              <div>
                <h4 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2 mb-3">
                  <i className="fas fa-shopping-cart text-primary" />
                  {text.totalSale} - Summary
                </h4>
                {soldMilkSummaryData.length > 0 ? (
                  <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700">
                            Milk Type
                          </th>
                          <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700">
                            Total Liters
                          </th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">
                            Total Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {soldMilkSummaryData.map((item) => (
                          <tr
                            key={item.milkTypeId}
                            onClick={() =>
                              setExpandedSalesSection(
                                expandedSalesSection === "soldMilk"
                                  ? null
                                  : "soldMilk",
                              )
                            }
                            className="border-b border-slate-200 dark:border-slate-700 hover:bg-primary/10 dark:hover:bg-primary/20 cursor-pointer transition"
                          >
                            <td className="px-4 py-2.5 text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700">
                              {item.milkTypeName}
                            </td>
                            <td className="px-4 py-2.5 text-center font-semibold text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700">
                              {item.totalLiters.toFixed(2)} L
                            </td>
                            <td className="px-4 py-2.5 text-right font-semibold text-slate-900 dark:text-white">
                              ₹{item.totalAmount.toLocaleString("en-IN")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    No milk sales for this period.
                  </p>
                )}
              </div>

              {/* Other Sales section - Always visible */}
              <div>
                <h4 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2 mb-3">
                  <i className="fas fa-tags text-primary" />
                  {text.otherSales}
                </h4>
                {otherSalesData.length > 0 ? (
                  <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700">
                            Product Name
                          </th>
                          <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300">
                            Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {otherSalesData.map((item) => (
                          <tr
                            key={item.salesId}
                            className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
                          >
                            <td className="px-4 py-2.5 text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700">
                              {item.productName}
                            </td>
                            <td className="px-4 py-2.5 text-right font-semibold text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700">
                              ₹{item.amount.toLocaleString("en-IN")}
                            </td>
                            <td className="px-4 py-2.5 text-center text-slate-500 dark:text-slate-400">
                              {new Date(item.entryDate).toLocaleDateString(
                                "en-IN",
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    No other sales for this period.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Dashboard;
